import { useState } from 'react';
import { motion } from 'framer-motion';
import { getFrameImageUrl } from '@/services/shotService';
import { MULTI_FRAME } from '@/constants/shotSettings';
import { Film, ChevronRight, Loader2, AlertCircle } from 'lucide-react';

const MOVEMENT_ICONS = {
  pan: '↔', tilt: '↕', 'zoom-in': '🔍', 'zoom-out': '🔎',
  dolly: '🎬', truck: '🚚', pedestal: '⬆', tracking: '🏃',
  crane: '🏗', handheld: '📹', steadicam: '🎥', arc: '↩', drone: '🚁',
};

export default function FrameFilmstrip({ frames, movementType, onFrameClick }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const multiFrame = movementType ? MULTI_FRAME[movementType] : null;
  const labels = multiFrame?.labels || frames.map((_, i) => `Frame ${i + 1}`);
  const icon = MOVEMENT_ICONS[movementType] || '🎬';

  return (
    <div
      className="card p-0 overflow-hidden"
      data-testid={`filmstrip-${frames[0]?.shot_group_id || frames[0]?.id}`}
    >
      {/* Sprocket top edge */}
      <div className="h-2 bg-[var(--bg-overlay)] flex items-center px-2 gap-3">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="w-2 h-1 rounded-full bg-[var(--bg-muted)]" />
        ))}
      </div>

      {/* Header badge */}
      <div className="px-3 py-1.5 flex items-center gap-2 border-b border-[var(--border-subtle)]/50 bg-[var(--bg-surface)]">
        <span className="text-sm">{icon}</span>
        <span className="text-[10px] font-semibold text-[var(--text-primary)] capitalize">
          {movementType?.replace('-', ' ') || 'Multi-frame'}
        </span>
        <span className="text-[9px] text-[var(--text-disabled)]">
          {frames.length} frame{frames.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Frames strip */}
      <div className="flex gap-0.5 p-2 overflow-x-auto bg-[var(--bg-muted)]/30">
        {frames.map((frame, idx) => {
          const isCompleted = frame.status === 'completed';
          const isGenerating = frame.status === 'generating';
          const isFailed = frame.status === 'failed';

          return (
            <div key={frame.id} className="flex items-center">
              <motion.div
                whileHover={{ y: -2, scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="relative cursor-pointer group shrink-0"
                onClick={() => onFrameClick?.(frame)}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                {/* Label above */}
                <p className="text-[8px] font-medium text-[var(--text-disabled)] text-center mb-0.5 uppercase tracking-wider">
                  {labels[idx] || `F${idx + 1}`}
                </p>

                {/* Frame image */}
                <div className="w-36 aspect-video rounded-lg overflow-hidden bg-[var(--bg-muted)] border border-[var(--border-subtle)] group-hover:border-[var(--color-gold-500)]/50 transition-colors">
                  {isCompleted ? (
                    <img
                      src={getFrameImageUrl(frame.id)}
                      alt={`${movementType} ${labels[idx]}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      crossOrigin="use-credentials"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {isGenerating ? (
                        <Loader2 size={16} className="animate-spin text-[var(--color-gold-400)]" />
                      ) : isFailed ? (
                        <AlertCircle size={16} className="text-[var(--color-error)]" />
                      ) : (
                        <Film size={16} className="text-[var(--text-disabled)]" />
                      )}
                    </div>
                  )}
                </div>

                {/* Shot info */}
                <div className="flex items-center justify-between mt-0.5 px-0.5">
                  <span className="text-[9px] text-[var(--text-muted)]">Shot {frame.frame_number}</span>
                  {frame.stage && (
                    <span className="text-[8px] text-[var(--text-disabled)]">{frame.stage}</span>
                  )}
                </div>
              </motion.div>

              {/* Arrow connector between frames */}
              {idx < frames.length - 1 && (
                <div className="px-1 flex items-center justify-center self-center">
                  <ChevronRight size={12} className="text-[var(--text-disabled)]" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Sprocket bottom edge */}
      <div className="h-2 bg-[var(--bg-overlay)] flex items-center px-2 gap-3">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="w-2 h-1 rounded-full bg-[var(--bg-muted)]" />
        ))}
      </div>
    </div>
  );
}
